/* eslint-env browser */
// Landmarks tab — extracted from the legacy SecurityDashboard (wave 3:
// cards, tokens, the shared confirmation dialog).
//
// Trigger UI for the import_addis_landmarks Cloud Function. The current
// landmark count is loaded only on explicit user click (avoids a full
// /landmarks read on tab open). The calls are unchanged: POST
// <host>/import_addis_landmarks_osm or /import_addis_landmarks with
// { dryRun } and the admin's ID token; a real Google import asks first.

import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ref, get } from 'firebase/database';
import { FirebaseContext } from 'common';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DashboardCard from '../../components/DashboardCard';
import { confirmDialog, InlineMessage, EmptyState } from '../../components/feedback';
import { SectionCard, FieldLabel, StatusChip, formatNumber } from '../../components/modern';
import { tokens as c } from '../../theme';

export default function Landmarks() {
  const { t } = useTranslation();
  const { database, auth: firebaseAuth, config } = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const settingsData = useSelector((state) => state.settingsdata);
  const isAdmin = auth?.profile?.usertype === 'admin';

  const [importingLandmarks, setImportingLandmarks] = useState(false);
  // Tracks which endpoint is currently importing so spinners + button
  // states stay scoped — clicking Google shouldn't grey out the OSM
  // button if it just happens to be running first.
  const [importingSource, setImportingSource] = useState(null); // 'google' | 'osm'
  const [landmarkResult, setLandmarkResult] = useState(null);
  const [landmarkDryRun, setLandmarkDryRun] = useState(true);
  const [currentLandmarkCount, setCurrentLandmarkCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);

  // Reads the count from /landmarks_meta/count — a leaf integer
  // maintained by both importers. Previously this read the entire
  // /landmarks node and Object.keys()'d the result, which would have
  // become a ~10 MB egress per click once OSM data is loaded
  // (~30-50k entries). Now it's ~10 bytes regardless of dataset size.
  //
  // Fallback: if /landmarks_meta/count isn't set yet (fresh DB, or
  // import predates the meta-counter change), fall back to the old
  // full-read path so admins still get a number rather than a dash.
  // That fallback never runs after the first import.
  const loadCurrentLandmarkCount = async () => {
    if (!database) return;
    setCountLoading(true);
    try {
      const metaSnap = await get(ref(database, 'landmarks_meta/count'));
      if (metaSnap.exists()) {
        const cnt = metaSnap.val();
        setCurrentLandmarkCount(typeof cnt === 'number' ? cnt : parseInt(cnt, 10) || 0);
        return;
      }
      // Cold cache fallback — only hits before the first import that
      // includes meta-counter writes lands.
      const snap = await get(ref(database, 'landmarks'));
      const data = snap.val();
      setCurrentLandmarkCount(data ? Object.keys(data).length : 0);
    } catch (_) {
      /* ignore — not critical */
    } finally {
      setCountLoading(false);
    }
  };

  // Shared driver for both importers. `endpoint` is the Cloud Function
  // path (import_addis_landmarks | import_addis_landmarks_osm),
  // `source` is the label tracked in state ('google' | 'osm') so the
  // UI can spinner the right button. Both functions return JSON with
  // {dryRun, written, uniqueLandmarks, ...} on success — the result
  // display below switches on `result.source` to render the correct
  // fields (Google has cellsScanned/apiCalls; OSM has byCategory).
  const runImport = async (endpoint, source) => {
    setImportingLandmarks(true);
    setImportingSource(source);
    setLandmarkResult(null);

    try {
      const currentUser = firebaseAuth.currentUser;
      if (!currentUser) {
        setLandmarkResult({ error: 'Not authenticated', source });
        return;
      }

      const idToken = await currentUser.getIdToken();
      const settings = settingsData?.settings;
      const host =
        typeof window !== 'undefined' && window.location && settings?.CompanyWebsite === window.location.origin
          ? window.location.origin
          : `https://${config?.projectId || 'donkey-drive-5cdd1'}.web.app`;

      const response = await fetch(`${host}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ dryRun: landmarkDryRun }),
      });

      const result = await response.json();

      if (response.ok) {
        setLandmarkResult({ success: true, source, ...result });
        if (!landmarkDryRun) {
          loadCurrentLandmarkCount();
        }
      } else {
        // Propagate the full error payload (error + detail + hint)
        // so the failure-render path can surface all of it.
        setLandmarkResult({
          error: result.error || 'Import failed',
          detail: result.detail,
          hint: result.hint,
          source,
        });
      }
    } catch (error) {
      console.error(`[${source}] Landmark import failed:`, error);
      setLandmarkResult({ error: error.message, source });
    } finally {
      setImportingLandmarks(false);
      setImportingSource(null);
    }
  };

  const handleLandmarkImport = () => runImport('import_addis_landmarks', 'google');
  const handleOsmImport = () => runImport('import_addis_landmarks_osm', 'osm');

  // Google Places costs money: a real import asks first (a dry run does not).
  const handleGoogleClick = async () => {
    if (landmarkDryRun) {
      handleLandmarkImport();
      return;
    }
    const ok = await confirmDialog({
      title: t('landmarks_confirm_title', { defaultValue: 'Confirm Real Landmark Import' }),
      body: t('landmarks_confirm_body', {
        defaultValue:
          'This will call the Google Places API ~350 times at ~$0.032 each — estimated cost ~$11 for this run. It imports POIs (hospitals, schools, mosques, hotels, fuel stations, banks, embassies, etc.) from a 5×5 grid centered on Meskel Square. Existing landmarks marked manualEdit: true are preserved. Nothing is deleted. The job takes 30–60 seconds.',
      }),
      confirmLabel: t('landmarks_confirm_run', { defaultValue: 'Run Real Import (~$11)' }),
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (ok) handleLandmarkImport();
  };

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState icon={<LockOutlinedIcon />} title={t('admin_access_required', { defaultValue: 'Admin access required.' })} />
      </Paper>
    );
  }

  const r = landmarkResult;
  const pill = (text) => (
    <Box component="span" sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: '6px', fontSize: 12, bgcolor: c.SURFACE_ALT, color: c.INK }}>
      {text}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <InlineMessage kind="info">
        {t('landmarks_info', {
          defaultValue:
            'Bulk-imports POIs (hospitals, schools, mosques, hotels, fuel stations, etc.) into /landmarks. Two sources: OpenStreetMap is free and gives 10-100× more POIs, Google Places has higher data quality for big-brand places. Both can run together; records are keyed separately so they coexist. After an import, run yarn export:landmarks and redeploy to bundle the new data into the apps.',
        })}
      </InlineMessage>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 2fr)' } }}>
        <DashboardCard
          icon={<PlaceOutlinedIcon />}
          hue="accent"
          title={t('landmarks_in_db', { defaultValue: 'Landmarks in Database' })}
          value={currentLandmarkCount === null ? '—' : formatNumber(currentLandmarkCount)}
        />
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, justifyContent: 'center' }}>
          <Box>
            <Button
              variant="outlined"
              color="neutral"
              startIcon={countLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshRoundedIcon />}
              onClick={loadCurrentLandmarkCount}
              disabled={countLoading}
            >
              {currentLandmarkCount === null ? t('landmarks_load_count', { defaultValue: 'Load count' }) : t('refresh', { defaultValue: 'Refresh' })}
            </Button>
          </Box>
          <Box>
            <FieldLabel id="landmarks-mode-label">{t('phone_cards_mode', { defaultValue: 'Mode' })}</FieldLabel>
            <ToggleButtonGroup
              exclusive
              color="primary"
              value={landmarkDryRun ? 'dry' : 'real'}
              onChange={(e, v) => {
                if (v) setLandmarkDryRun(v === 'dry');
              }}
              aria-labelledby="landmarks-mode-label"
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value="dry" sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}>
                {t('dry_run_preview', { defaultValue: 'Dry run (preview only — no writes)' })}
              </ToggleButton>
              <ToggleButton value="real" sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}>
                {t('landmarks_real_import', { defaultValue: 'Real import (writes to /landmarks)' })}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Paper>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
        {/* OSM importer card — free, broad coverage. Listed first so it's the
            obvious choice (Google is kept as backup). */}
        <SectionCard id="landmarks-osm" icon={<PublicOutlinedIcon />} hue="success" title="OpenStreetMap">
          <Typography variant="body2" sx={{ color: c.INK_2, mb: 2 }}>
            {t('landmarks_osm_desc', { defaultValue: 'Free · ~30-50k POIs across all of Addis · Single Overpass query, finishes in 30-60s' })}
          </Typography>
          <Button
            variant="contained"
            color="success"
            fullWidth
            startIcon={importingSource === 'osm' ? <CircularProgress size={18} color="inherit" /> : <PublicOutlinedIcon />}
            disabled={importingLandmarks}
            onClick={handleOsmImport}
          >
            {importingSource === 'osm'
              ? landmarkDryRun
                ? t('previewing', { defaultValue: 'Previewing…' })
                : t('importing', { defaultValue: 'Importing…' })
              : landmarkDryRun
                ? t('landmarks_preview_osm', { defaultValue: 'Preview from OSM' })
                : t('landmarks_import_osm', { defaultValue: 'Import from OSM (free)' })}
          </Button>
        </SectionCard>

        {/* Google Places importer — kept as a backup. ~$11/run so gated behind
            a confirmation for the real import. */}
        <SectionCard id="landmarks-google" icon={<MapOutlinedIcon />} hue="amber" title="Google Places">
          <Typography variant="body2" sx={{ color: c.INK_2, mb: 2 }}>
            {t('landmarks_google_desc', { defaultValue: '~$11/run · ~350 API calls · Higher quality for chain brands · 5×5 grid around Meskel Square' })}
          </Typography>
          <Button
            variant={landmarkDryRun ? 'outlined' : 'contained'}
            color={landmarkDryRun ? 'neutral' : 'error'}
            fullWidth
            startIcon={importingSource === 'google' ? <CircularProgress size={18} color="inherit" /> : <PlayArrowRoundedIcon />}
            disabled={importingLandmarks}
            onClick={handleGoogleClick}
          >
            {importingSource === 'google'
              ? landmarkDryRun
                ? t('previewing', { defaultValue: 'Previewing…' })
                : t('importing', { defaultValue: 'Importing…' })
              : landmarkDryRun
                ? t('landmarks_preview_google', { defaultValue: 'Preview from Google' })
                : t('landmarks_import_google', { defaultValue: 'Import from Google (~$11)' })}
          </Button>
        </SectionCard>
      </Box>

      {r ? (
        r.success ? (
          <InlineMessage
            kind="success"
            title={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                {r.dryRun ? t('landmarks_dry_done', { defaultValue: 'Dry run complete' }) : t('landmarks_import_done', { defaultValue: 'Import complete' })}
                <StatusChip tone={r.source === 'osm' ? 'success' : 'amber'} label={r.source === 'osm' ? 'OpenStreetMap' : 'Google Places'} />
              </Box>
            }
            onClose={() => setLandmarkResult(null)}
          >
            <Box sx={{ mt: 0.5 }}>
              {/* Google-import-specific stats */}
              {r.source !== 'osm' ? (
                <>
                  {t('landmarks_cells', { defaultValue: 'Cells scanned' })}: <strong>{r.cellsScanned}</strong> · {t('landmarks_categories', { defaultValue: 'Categories' })}:{' '}
                  <strong>{r.categoriesScanned}</strong> · {t('geo_api_calls', { defaultValue: 'API calls' })}: <strong>{r.apiCalls}</strong> ·{' '}
                </>
              ) : null}
              {t('landmarks_unique', { defaultValue: 'Unique landmarks found' })}: <strong>{r.uniqueLandmarks}</strong>
              {!r.dryRun ? (
                <>
                  {' · '}
                  {t('landmarks_written', { defaultValue: 'Written' })}: <strong>{r.written}</strong>
                  {' · '}
                  {t('landmarks_preserved', { defaultValue: 'Manual edits preserved' })}: <strong>{r.preservedManual}</strong>
                </>
              ) : null}
              {r.errorCount > 0 ? (
                <>
                  {' · '}
                  {t('errors', { defaultValue: 'Errors' })}: <strong>{r.errorCount}</strong>
                </>
              ) : null}
            </Box>
            {/* OSM-specific: per-category breakdown so admin can spot any bucket
                that came back surprisingly empty. */}
            {r.source === 'osm' && r.byCategory ? (
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {Object.entries(r.byCategory)
                  .sort((a, b) => b[1] - a[1])
                  .map(([cat, count]) => (
                    <Box key={cat}>
                      {pill(
                        <>
                          {cat}: <strong>{count}</strong>
                        </>
                      )}
                    </Box>
                  ))}
              </Box>
            ) : null}
            {r.errorCount > 0 && Array.isArray(r.errors) && r.errors.length > 0 ? (
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: c.WARNING_TINT, color: c.WARNING_TEXT, borderRadius: '8px' }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {t('landmarks_first_error', { count: r.errorCount, defaultValue: 'First error (of {{count}}):' })}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
                  {r.errors[0]}
                </Typography>
              </Box>
            ) : null}
            {r.dryRun && Array.isArray(r.sample) && r.sample.length > 0 ? (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {t('landmarks_sample', { n: r.sample.length, total: r.uniqueLandmarks, defaultValue: 'Sample of {{n}} (out of {{total}}):' })}
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 0, mt: 0.5 }}>
                  {r.sample.map((s) => (
                    <li key={s.id}>
                      <Typography variant="caption">
                        <strong>{s.name}</strong> ({s.category}) — {s.area || `${s.lat?.toFixed(4)}, ${s.lng?.toFixed(4)}`}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            ) : null}
            {!r.dryRun ? (
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                {t('landmarks_next_step', {
                  defaultValue: 'Next step: run yarn export:landmarks from the repo root, then yarn app:publish + yarn web:deploy to bundle the new data.',
                })}
              </Typography>
            ) : null}
          </InlineMessage>
        ) : (
          // Failure path — the headline error AND the detail/hint when
          // available (the OSM importer returns both when an Overpass mirror
          // chain fails, so the admin can see what each endpoint said).
          <InlineMessage kind="error" title={r.error} onClose={() => setLandmarkResult(null)}>
            {r.detail ? (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {r.detail}
              </Typography>
            ) : null}
            {r.hint ? (
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                {r.hint}
              </Typography>
            ) : null}
          </InlineMessage>
        )
      ) : null}
    </Box>
  );
}
